import { createFileRoute, Link } from "@tanstack/react-router";
import { usePricingLabels } from "@/lib/pricing-labels";
import { priceWithInterval } from "@/lib/commercial-copy";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms & Conditions — Routify" },
      { name: "description", content: "Routify Terms & Conditions / Termos e Condições de utilização." },
    ],
  }),
  component: TermsPage,
});

function TermsPage() {
  const pricing = usePricingLabels();
  const premiumMonthly = priceWithInterval("premium_monthly", pricing, "en");
  const proMonthly = priceWithInterval("pro_monthly", pricing, "en");
  const premiumMonthlyPt = priceWithInterval("premium_monthly", pricing, "pt");
  const proMonthlyPt = priceWithInterval("pro_monthly", pricing, "pt");
  return (
    <main className="mx-auto max-w-3xl px-6 py-16 text-foreground">
      <Link to="/legal" className="text-sm text-muted-foreground hover:text-foreground">← Legal</Link>
      <h1 className="mt-6 text-3xl font-semibold">Terms &amp; Conditions</h1>
      <p className="mt-2 text-xs text-muted-foreground">Last updated / Última atualização: 2026-07-19 · Version 2.1</p>

      <section className="mt-8 space-y-5 text-sm leading-relaxed">
        <h2 className="text-lg font-semibold">1. Provider and contact</h2>
        <p>
          This service ("Routify", the "Service") is operated by the entity identified on the{" "}
          <Link to="/legal" className="underline">Legal page</Link>. All notices and complaints
          should be sent to <a className="underline" href="mailto:Routify.info@gmail.com">Routify.info@gmail.com</a>.
        </p>

        <h2 className="text-lg font-semibold">2. Scope and acceptance</h2>
        <p>
          By accessing or using the Service you agree to these Terms, our{" "}
          <Link to="/privacy" className="underline">Privacy Policy</Link> and our{" "}
          <Link to="/cookies" className="underline">Cookie Policy</Link>. If you do not agree,
          you must not use the Service. These Terms form a binding contract between you and the
          provider.
        </p>

        <h2 className="text-lg font-semibold">3. Service description</h2>
        <p>
          Routify is an online tool that generates tourist itinerary suggestions using
          artificial intelligence (large language models) combined with third-party mapping data.
          The Service is provided remotely, on demand, and accessed through a web browser. No
          physical goods are delivered.
        </p>
        <p>
          Routify also offers an optional <strong>custom planning service</strong>
          in which our team prepares a custom itinerary against a quote communicated by email
          before payment. Custom planning requests are handled within approximately 24 hours and only
          become chargeable after you accept the quote and complete payment via Stripe.
        </p>

        <h2 className="text-lg font-semibold">4. AI-generated content — important disclaimer</h2>
        <p>
          Itineraries, route descriptions, opening hours, prices, accessibility notes and
          restaurant suggestions are <strong>generated automatically and may be inaccurate,
          incomplete or outdated</strong>. You are solely responsible for verifying any
          information that materially affects your trip (opening hours, prices, safety,
          accessibility, transport, weather, restrictions). The Service is informational and is
          not a substitute for official sources or professional travel advice.
        </p>

        <h2 className="text-lg font-semibold">5. User accounts</h2>
        <ul className="list-disc pl-5">
          <li>You may create an account using email/password or supported single sign-on providers.</li>
          <li>You must provide accurate information and keep your credentials confidential.</li>
          <li>You are responsible for all activity carried out under your account.</li>
          <li>You must be at least 16 years old, or the minimum digital consent age in your country.</li>
          <li>We may suspend or terminate accounts that violate these Terms, with or without notice when required to protect users or the Service.</li>
        </ul>

        <h2 className="text-lg font-semibold">6. Free plan, Premium, Pro, custom planning and credits</h2>
        <ul className="list-disc pl-5">
          <li>Basic itinerary generation is offered free of charge, subject to fair-use quotas to prevent abuse.</li>
          <li>Premium features (saving itineraries, exporting, audio guide, higher quotas) are offered through a subscription of {premiumMonthly}, VAT included where applicable. Subscriptions renew automatically until cancelled.</li>
          <li><strong>Routify Pro</strong> (advanced multi-day / multi-city planner with AI optimisation) is offered through a separate subscription of {proMonthly}, VAT included where applicable, and also renews automatically until cancelled.</li>
          <li><strong>Custom planning</strong> is billed per request, based on the quote we send you by email after analysing your brief. There is no automatic charge: the request only becomes payable once you accept the quote and pay through the Stripe link provided.</li>
          <li>Itinerary credits, where offered, are non-refundable digital items consumed on use and expire as indicated at purchase.</li>
          <li>You can cancel any subscription at any time from your account; access remains active until the end of the paid period.</li>
          <li>Prices may change. Changes do not affect billing cycles already paid; we will notify you in advance for renewals.</li>
          <li>Payments are processed by Stripe (Stripe Payments Europe, Ltd.). We do not store full card data.</li>
        </ul>

        <h2 className="text-lg font-semibold">6a. Third-party mapping data (Google Maps)</h2>
        <p>
          Certain features (maps, place search, directions) rely on Google Maps Platform. By
          using those features you are also bound by the{" "}
          <a className="underline" href="https://cloud.google.com/maps-platform/terms" target="_blank" rel="noreferrer">Google Maps/Google Earth Additional Terms of Service</a>{" "}
          and the{" "}
          <a className="underline" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Google Privacy Policy</a>.
        </p>

        <h2 className="text-lg font-semibold">6b. Partner Programme</h2>
        <p>
          Routify operates a <strong>Partner Programme</strong> for content creators and
          influencers. Applications are submitted at <Link to="/partner" className="underline">/partner</Link>{" "}
          and, if approved, grant the partner a personal promo code and referral link that
          entitle their audience to a discount and the partner to a commission on eligible sales.
          Participation in the Programme is governed by the dedicated{" "}
          <Link to="/partner-terms" className="underline">Partner Programme Terms</Link>, which
          form an integral part of these Terms for approved partners and set out the rules on
          eligibility, commissions, brand use, anti-fraud and payouts.
        </p>

        <h2 className="text-lg font-semibold">7. Right of withdrawal (EU consumers)</h2>
        <p>
          Pursuant to Article 16(m) of Directive 2011/83/EU and Article 17(1)(m) of Portuguese
          Decree-Law 24/2014, by purchasing a digital subscription or credits that begin to be
          executed immediately, <strong>you expressly request execution during the 14-day
          withdrawal period and acknowledge that the right of withdrawal is lost</strong> once
          the service has begun and the credits become available in your account.
        </p>

        <h2 className="text-lg font-semibold">8. Acceptable use</h2>
        <p>You agree not to:</p>
        <ul className="list-disc pl-5">
          <li>Use the Service for unlawful, fraudulent or harmful purposes;</li>
          <li>Reverse-engineer, decompile, scrape, mirror or systematically extract content;</li>
          <li>Submit prompts or content that violate third-party rights, defame, harass, or generate illegal content;</li>
          <li>Use automated means (bots, crawlers) without prior written authorisation;</li>
          <li>Circumvent quotas, paywalls, authentication or rate limits;</li>
          <li>Resell or sublicense the Service or its output as your own commercial product.</li>
        </ul>

        <h2 className="text-lg font-semibold">9. Intellectual property</h2>
        <p>
          The Service, including its software, UI, branding, name and original content, is owned
          by the provider and protected by Portuguese and EU intellectual property law. We grant
          you a personal, non-exclusive, non-transferable, revocable licence to use the Service
          for its intended purpose. You retain the rights to the inputs you submit; you grant us
          a limited licence to process those inputs to provide the Service. AI-generated outputs
          may be used freely for your personal or professional travel planning, with the
          disclaimer in clause 4.
        </p>

        <h2 className="text-lg font-semibold">10. Third-party services</h2>
        <p>
          The Service relies on third-party providers (notably Google Maps Platform, OpenAI,
          Supabase/Lovable Cloud, Cloudflare, Stripe). Their availability, terms and processing
          activities are governed by their own agreements and are listed in our{" "}
          <Link to="/privacy" className="underline">Privacy Policy</Link>. We are not responsible
          for outages or content of third-party services.
        </p>

        <h2 className="text-lg font-semibold">11. Availability and modifications</h2>
        <p>
          We strive to keep the Service available but do not guarantee uninterrupted operation.
          We may add, change, suspend or discontinue features at any time. Material changes that
          adversely affect paying users will be communicated in advance; you may cancel and
          receive a pro-rata refund for any unused portion of the current paid period.
        </p>

        <h2 className="text-lg font-semibold">12. Liability</h2>
        <p>
          The Service is provided "as is" and "as available". To the maximum extent permitted by
          law, the provider's aggregate liability for any claim arising out of or in connection
          with the Service is limited to the amount you paid in the 12 months preceding the
          event. The provider is not liable for indirect, incidental, special, consequential or
          punitive damages, including loss of profit, data, opportunity, or travel costs
          incurred based on AI-generated content. Nothing in these Terms excludes liability that
          cannot be excluded under mandatory consumer protection law.
        </p>

        <h2 className="text-lg font-semibold">13. Indemnity</h2>
        <p>
          You agree to indemnify and hold the provider harmless from third-party claims arising
          from your misuse of the Service or breach of these Terms, save where prohibited by law.
        </p>

        <h2 className="text-lg font-semibold">14. Force majeure</h2>
        <p>
          Neither party is liable for delay or failure to perform caused by events beyond
          reasonable control, including natural disasters, war, civil unrest, strikes, internet
          outages, infrastructure failures of cloud providers, or governmental measures.
        </p>

        <h2 className="text-lg font-semibold">15. Assignment</h2>
        <p>
          You may not assign these Terms without our written consent. We may assign these Terms
          to an affiliate or successor in connection with a reorganisation or transfer of the
          business.
        </p>

        <h2 className="text-lg font-semibold">16. Severability</h2>
        <p>
          If any provision is held invalid or unenforceable, the remaining provisions remain in
          full force.
        </p>

        <h2 className="text-lg font-semibold">17. Changes to these Terms</h2>
        <p>
          We may update these Terms from time to time. The "Last updated" date above shows the
          current version. Material changes will be notified by email or in-app. Continued use
          after the effective date constitutes acceptance.
        </p>

        <h2 className="text-lg font-semibold">18. Governing law and jurisdiction</h2>
        <p>
          These Terms are governed by Portuguese law. Disputes shall be submitted to the
          competent Portuguese courts, without prejudice to mandatory consumer rights and to the
          alternative dispute resolution entities listed on the{" "}
          <Link to="/legal" className="underline">Legal page</Link>.
        </p>

        <hr className="my-8 border-border" />

        <h2 className="text-xl font-semibold">Versão em Português</h2>

        <h3 className="text-base font-semibold mt-4">1. Prestador e contacto</h3>
        <p>
          O Serviço é operado pela entidade identificada na página{" "}
          <Link to="/legal" className="underline">Legal</Link>. Comunicações e reclamações:{" "}
          <a className="underline" href="mailto:Routify.info@gmail.com">Routify.info@gmail.com</a>.
        </p>

        <h3 className="text-base font-semibold mt-4">2. Âmbito e aceitação</h3>
        <p>
          Ao utilizar o Serviço aceita estes Termos, a{" "}
          <Link to="/privacy" className="underline">Política de Privacidade</Link> e a{" "}
          <Link to="/cookies" className="underline">Política de Cookies</Link>. Se não concorda,
          não deve utilizar o Serviço.
        </p>

        <h3 className="text-base font-semibold mt-4">3. Descrição do Serviço</h3>
        <p>
          O Routify gera sugestões de itinerários turísticos com recurso a inteligência
          artificial e a dados de mapas de terceiros. É prestado à distância, online, e não
          envolve a entrega de bens físicos.
        </p>
        <p>
          O Routify oferece ainda um <strong>serviço opcional de planeamento personalizado</strong>
          em que a equipa prepara uma rota à medida, mediante orçamento
          comunicado por email antes do pagamento. Os pedidos de planeamento personalizado são analisados em cerca
          de 24 horas e só se tornam exigíveis após a aceitação do orçamento e o pagamento via
          Stripe.
        </p>

        <h3 className="text-base font-semibold mt-4">4. Conteúdos gerados por IA</h3>
        <p>
          Itinerários, horários, preços, acessibilidades e sugestões são gerados automaticamente
          e <strong>podem conter erros</strong>. O utilizador é responsável por confirmar em
          fontes oficiais qualquer informação relevante (horários, segurança, transportes,
          restrições, etc.). O Serviço tem natureza meramente informativa.
        </p>

        <h3 className="text-base font-semibold mt-4">5. Contas de utilizador</h3>
        <ul className="list-disc pl-5">
          <li>Pode criar conta por email/password ou via fornecedores de SSO suportados;</li>
          <li>É responsável pela confidencialidade das credenciais e pela atividade da conta;</li>
          <li>Tem de ter pelo menos 16 anos (ou idade legal mínima no seu país);</li>
          <li>Podemos suspender contas que violem estes Termos.</li>
        </ul>

        <h3 className="text-base font-semibold mt-4">6. Plano gratuito, Premium, Pro, planeamento personalizado e créditos</h3>
        <ul className="list-disc pl-5">
          <li>Geração base gratuita, sujeita a quotas de uso justo;</li>
          <li>Subscrição Premium de {premiumMonthlyPt} (IVA incluído quando aplicável), com renovação automática;</li>
          <li><strong>Routify Pro</strong> (planeador multi-dia/multi-cidade com otimização por IA) por subscrição separada de {proMonthlyPt}, IVA incluído quando aplicável, também com renovação automática;</li>
          <li><strong>Planeamento personalizado</strong> é faturado por pedido, com base no orçamento enviado por email após análise do brief; não há cobrança automática — só se torna devido após aceitação do orçamento e pagamento via link Stripe;</li>
          <li>Os créditos de itinerário são bens digitais não reembolsáveis, consumidos na utilização;</li>
          <li>Pode cancelar qualquer subscrição a qualquer momento; o acesso mantém-se até ao fim do período pago;</li>
          <li>Pagamentos processados pela Stripe; não armazenamos dados completos de cartão.</li>
        </ul>

        <h3 className="text-base font-semibold mt-4">6a. Dados de mapas de terceiros (Google Maps)</h3>
        <p>
          Certas funcionalidades (mapas, pesquisa de locais, direções) recorrem ao Google Maps
          Platform. Ao usá-las fica também vinculado aos{" "}
          <a className="underline" href="https://cloud.google.com/maps-platform/terms" target="_blank" rel="noreferrer">Termos Adicionais do Google Maps/Google Earth</a>{" "}
          e à{" "}
          <a className="underline" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Política de Privacidade da Google</a>.
        </p>

        <h3 className="text-base font-semibold mt-4">6b. Programa de Parceiros</h3>
        <p>
          A Routify tem um <strong>Programa de Parceiros</strong> destinado a criadores de
          conteúdo e influenciadores. As candidaturas são submetidas em{" "}
          <Link to="/partner" className="underline">/partner</Link> e, se aprovadas, atribuem ao
          parceiro um código promocional e link de referência que conferem desconto à sua
          audiência e comissão sobre as vendas elegíveis. A participação rege-se pelos{" "}
          <Link to="/partner-terms" className="underline">Termos do Programa de Parceiros</Link>,
          que fazem parte integrante destes Termos para os parceiros aprovados e definem as
          regras de elegibilidade, comissões, uso da marca, antifraude e pagamentos.
        </p>

        <h3 className="text-base font-semibold mt-4">7. Direito de livre resolução</h3>
        <p>
          Nos termos do art. 17.º, n.º 1, al. m) do DL 24/2014, ao adquirir uma subscrição ou
          créditos digitais de execução imediata o utilizador <strong>solicita expressamente o
          início da prestação e reconhece a perda do direito de livre resolução de 14 dias</strong>{" "}
          assim que o serviço se inicia e os créditos ficam disponíveis.
        </p>

        <h3 className="text-base font-semibold mt-4">8. Utilização aceitável</h3>
        <p>
          É proibido usar o Serviço para fins ilícitos, fraudulentos, lesivos de terceiros, ou
          recorrer a engenharia reversa, scraping, automação não autorizada, contorno de
          quotas/autenticação, ou revenda do Serviço.
        </p>

        <h3 className="text-base font-semibold mt-4">9. Propriedade intelectual</h3>
        <p>
          O Serviço (software, marca, conteúdos originais) pertence ao prestador. É concedida ao
          utilizador uma licença pessoal, não exclusiva e revogável para utilização normal. O
          utilizador mantém os direitos sobre os inputs que submete e concede licença limitada
          de processamento para prestação do Serviço.
        </p>

        <h3 className="text-base font-semibold mt-4">10. Terceiros</h3>
        <p>
          O Serviço depende de fornecedores terceiros (Google Maps, OpenAI, Supabase/Lovable
          Cloud, Cloudflare, Stripe) sujeitos aos respetivos termos. Não respondemos por
          indisponibilidade desses serviços.
        </p>

        <h3 className="text-base font-semibold mt-4">11. Disponibilidade e alterações</h3>
        <p>
          Não garantimos funcionamento ininterrupto. Podemos alterar, suspender ou descontinuar
          funcionalidades. Alterações materiais desfavoráveis a utilizadores Premium serão
          comunicadas com antecedência, com direito a cancelamento e reembolso proporcional.
        </p>

        <h3 className="text-base font-semibold mt-4">12. Responsabilidade</h3>
        <p>
          O Serviço é prestado "tal como está". Na máxima medida permitida por lei, a
          responsabilidade agregada do prestador limita-se ao montante pago pelo utilizador nos
          12 meses anteriores ao facto gerador. Excluem-se danos indiretos, lucros cessantes,
          perda de dados ou custos de viagem incorridos com base em conteúdos gerados por IA. Não
          se afastam direitos imperativos do consumidor.
        </p>

        <h3 className="text-base font-semibold mt-4">13. Indemnização</h3>
        <p>
          O utilizador responde por danos causados a terceiros decorrentes do uso indevido do
          Serviço, salvo quando proibido por lei.
        </p>

        <h3 className="text-base font-semibold mt-4">14. Força maior</h3>
        <p>
          Nenhuma das partes responde por incumprimentos resultantes de causas alheias ao seu
          controlo razoável (catástrofes, guerra, greves, falhas de internet ou de fornecedores
          cloud, medidas governamentais).
        </p>

        <h3 className="text-base font-semibold mt-4">15. Cessão</h3>
        <p>
          O utilizador não pode ceder estes Termos sem consentimento escrito. O prestador pode
          ceder em caso de reorganização ou transmissão do negócio.
        </p>

        <h3 className="text-base font-semibold mt-4">16. Invalidade parcial</h3>
        <p>A invalidade de qualquer cláusula não afeta a validade das restantes.</p>

        <h3 className="text-base font-semibold mt-4">17. Alterações aos Termos</h3>
        <p>
          Estes Termos podem ser atualizados. Alterações materiais serão notificadas. A
          utilização continuada após a entrada em vigor constitui aceitação.
        </p>

        <h3 className="text-base font-semibold mt-4">18. Lei aplicável e jurisdição</h3>
        <p>
          Lei portuguesa. Foro: tribunais portugueses competentes, sem prejuízo dos direitos
          imperativos do consumidor e das entidades RAL indicadas na página{" "}
          <Link to="/legal" className="underline">Legal</Link>.
        </p>
      </section>
    </main>
  );
}
